import React, { createContext, useContext } from "react";
import type * as Y from "yjs";

/**
 * Live-collaboration wiring for page-document text blocks (Phase 3a).
 *
 * The host app (apps/web PageDocumentEditor) owns the Y.Doc + network provider
 * — created once per open document, keyed by the page OID — and drops them into
 * this context. Each text block then binds its TipTap editor to a per-block
 * Y.XmlFragment (see `usePageTextBlock` → PageRichText). When no value is
 * provided (forms, non-collab hosts), blocks render exactly as before.
 *
 * The package stays app-agnostic about transport: the provider is only required
 * to expose an awareness object (for cursors) and a `whenSynced` promise (so a
 * block can seed an empty fragment from its stored HTML exactly once, after the
 * initial sync, rather than racing the server's state).
 */

export interface PageCollabUser {
  /** Display name shown on the remote caret label. */
  name: string;
  /** Caret + selection colour. */
  color: string;
}

export interface PageCollabProviderLike {
  /** y-protocols Awareness instance driving remote cursors/presence. */
  awareness: unknown;
  /** Resolves once the doc has synced with the server at least once. */
  whenSynced: Promise<void>;
}

export interface PageCollabValue {
  provider: PageCollabProviderLike;
  user: PageCollabUser;
  /**
   * Resolve the Y.XmlFragment a text block's editor binds to. The host owns the
   * block model and returns the block's content fragment (or null if the block
   * isn't a collaborative text surface / doesn't exist yet).
   */
  getContentFragment: (elementId: string) => Y.XmlFragment | null;
  /**
   * Presence: the block the local user is currently editing (focused editor),
   * or null on blur. The host broadcasts it via awareness so collaborators can
   * show a "who's editing what" indicator against the block.
   */
  setEditingBlockId?: (elementId: string | null) => void;
}

const PageCollabContext = createContext<PageCollabValue | null>(null);

export const PageCollabRoot: React.FC<{
  value: PageCollabValue | null;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <PageCollabContext.Provider value={value}>
    {children}
  </PageCollabContext.Provider>
);

/** The active collaboration session, or null when the doc isn't collaborative. */
export function usePageCollab(): PageCollabValue | null {
  return useContext(PageCollabContext);
}
